// Fields and model tools (phase M2c): the "Fields" block of a calculation (B, E; Peierls, Zeeman), the T(B, E) and
// lead-band calculations, the model modifiers (negf.modify) and the band-edge tools (negf.band_edges), with figures.

export const G_ELECTRON = 2.00231930436;
const MU_B_MEV = 0.057883818060;            // Bohr magneton in meV/T

// -- the field of a calculation ----------------------------------------------------------------------------------
/** spec.field of prayog_negf.jobs: B in tesla, E in V/Å (across the transport direction). */
export interface FieldForm {
  B: number[];
  E: number[];
  peierls: boolean;            // orbital motion: B in the hoppings (Landau levels, quantum Hall, Aharonov–Bohm)
  zeeman: boolean;             // spin (g μB/2) B·σ and orbital μB L·B
  orbital_zeeman: boolean;     // the μB L·B part (atomic orbitals of an SK model)
  g: number;
}
export function defaultField(): FieldForm {
  return { B: [0, 0, 0], E: [0, 0, 0], peierls: true, zeeman: true, orbital_zeeman: true, g: G_ELECTRON };
}
const nonZero = (v: number[] | undefined) => !!v && v.some((x) => x !== 0);
export const fieldActive = (f?: FieldForm) => !!f && (nonZero(f.B) || nonZero(f.E));

export type SweepDirection = "out-of-plane" | "in-plane" | "in-plane-2" | "transport";
export const SWEEP_DIRECTIONS: { value: SweepDirection; label: string }[] = [
  { value: "out-of-plane", label: "Out of plane (layer normal; z in 3D)" },
  { value: "in-plane", label: "In plane, along a1" },
  { value: "in-plane-2", label: "In plane, across a1" },
  { value: "transport", label: "Along the transport direction" },
];
export interface FieldSweepForm { B_min: number; B_max: number; n: number; direction: SweepDirection }
export const defaultFieldSweep = (): FieldSweepForm => ({ B_min: 0, B_max: 10, n: 11, direction: "out-of-plane" });

/** spec.field: the whole field (the server fills nothing in when the field is off). */
export function fieldSpec(f: FieldForm): Record<string, unknown> {
  return { B: f.B.map(Number), E: f.E.map(Number), peierls: f.peierls, zeeman: f.zeeman, orbital_zeeman: f.orbital_zeeman, g: f.g };
}

export function fieldProblems(f: FieldForm | undefined, sweep?: FieldSweepForm, transportAxis?: number, lattice?: number[][]): string[] {
  const out: string[] = [];
  if (f) {
    if ([...f.B, ...f.E].some((x) => !Number.isFinite(x))) out.push("Field: B and E are numbers.");
    if (!(f.g > 0)) out.push("Field: the g factor must be positive.");
    if (nonZero(f.E) && lattice && transportAxis != null) {
      const t = lattice[transportAxis];
      const n = Math.hypot(...t);
      const along = (f.E[0] * t[0] + f.E[1] * t[1] + f.E[2] * t[2]) / (n || 1);
      if (Math.abs(along) > 1e-9) out.push("Field: E must be across the transport direction (a bias drives the current along it).");
    }
  }
  if (sweep) {
    if (sweep.B_max === sweep.B_min) out.push("Field sweep: B from and to must differ.");
    if (!(sweep.n >= 2)) out.push("Field sweep: at least 2 field points.");
  }
  return out;
}

const vec = (v: number[]) => `(${v.map((x) => +x.toPrecision(4)).join(", ")})`;
/** "B (0, 0, 10) T, E (0, 0.01, 0) V/Å" for the list of runs ("" without a field). */
export function describeField(f: Record<string, any> | undefined): string {
  if (!f) return "";
  const parts: string[] = [];
  if (nonZero(f.B)) parts.push(`B ${vec(f.B)} T${f.peierls === false ? " (Zeeman only)" : f.zeeman === false ? " (orbital only)" : ""}`);
  if (nonZero(f.E)) parts.push(`E ${vec(f.E)} V/Å`);
  return parts.join(", ");
}

// -- figures of the field calculations --------------------------------------------------------------------------
/** T(B, E) as a map: Landau levels fan out, quantum Hall plateaus are flat bands of colour. */
export function fieldMapFigure(r: Record<string, any>) {
  return {
    data: [{ type: "heatmap", x: r.E, y: r.B, z: r.T, colorscale: "Viridis", colorbar: { title: { text: "T" } },
      hovertemplate: "E %{x:.4f} eV<br>B %{y:.4g} T<br>T %{z:.4f}<extra></extra>" }],
    layout: { title: { text: r.name || "Transmission in a magnetic field" }, xaxis: { title: { text: "E − E<sub>F</sub> (eV)" } },
      yaxis: { title: { text: `|B| (T) along ${vec(r.direction ?? [0, 0, 1])}` } } },
  };
}

/** T(E) at one field of a T(B, E) map. */
export function fieldCutFigure(r: Record<string, any>, i: number) {
  return {
    data: [
      { type: "scatter", mode: "lines", name: "T(E)", x: r.E, y: r.T[i], line: { color: "#2e213e", width: 2 } },
      { type: "scatter", mode: "lines", name: "open channels", x: r.E, y: r.channels?.[i], line: { color: "#9a9aa0", width: 1, dash: "dot", shape: "hv" } },
    ],
    layout: { title: { text: `T(E) at |B| = ${(+r.B[i]).toPrecision(4)} T` }, xaxis: { title: { text: "E − E<sub>F</sub> (eV)" } },
      yaxis: { title: { text: "Transmission T(E)" }, rangemode: "tozero" }, legend: { orientation: "h", y: -0.2 } },
  };
}

/** Bands as one trace (null between bands: fast with hundreds of bands). */
function bandTrace(x: number[], E: number[][], name: string, color: string, width = 1.2, dash?: string) {
  const xs: (number | null)[] = [], ys: (number | null)[] = [];
  const nb = E[0]?.length ?? 0;
  for (let b = 0; b < nb; b++) {
    for (let i = 0; i < x.length; i++) { xs.push(x[i]); ys.push(E[i][b]); }
    xs.push(null); ys.push(null);
  }
  return { type: "scatter", mode: "lines", name, x: xs, y: ys, line: { color, width, dash }, hoverinfo: "y" };
}

/** The lead's bands E(k) with the field (Landau bands, edge states). */
export function leadBandsFigure(r: Record<string, any>) {
  const e = r.spec?.energies;
  return {
    data: [bandTrace(r.k, r.bands, "lead bands", "#2e213e", 1)],
    layout: { title: { text: `${r.name || "Lead bands"}${describeField(r.field) ? ` · ${describeField(r.field)}` : ""}` },
      xaxis: { title: { text: "k along the lead (1/Å)" } },
      yaxis: { title: { text: "E − E<sub>F</sub> (eV)" }, range: e ? [e.min, e.max] : undefined }, showlegend: false },
  };
}

// -- model modifiers (negf.modify) -------------------------------------------------------------------------------
export interface ModifyForm {
  zeeman: { on: boolean; B: number[]; g: number; spin: boolean; orbital: boolean };
  mass: { on: boolean; delta: number; plus: number[]; minus: number[] };       // sublattice mass: +Δ/2 / −Δ/2
  shift: { on: boolean; shift: number; element: string; orbitals: string };     // on-site shift of some orbitals
  // mesh 0 = automatic (12 per direction: holds 1/2, 1/3, 1/4, 1/6); nValence 0 = the bands below E_F at every k
  scissor: { on: boolean; delta: number; mesh: number; nValence?: number };
  // exchange field (eV, along a direction) and Rashba coupling (eV, nearest-neighbour bonds): absent in older forms
  exchange?: { on: boolean; energy: number; direction: number[]; element: string };
  rashba?: { on: boolean; lam: number; orbitals: string };
  // a uniform electric field (V/Å) across the vacuum of a layer or wire (W4d: the interband terms from r(R))
  efield?: { on: boolean; E: number[] };
  // atomic spin-orbit coupling λ L·S on one element's p, d or f shell (a model fitted or Wannierised without SOC);
  // labels: the atomic orbitals of each site's functions when they are Wannier functions (e.g. "pz px py")
  soc?: { on: boolean; element: string; family: "p" | "d" | "f" | "all"; lam: number; labels: string };
}
export function defaultModify(nSites = 2): ModifyForm {
  return {
    zeeman: { on: false, B: [0, 0, 10], g: G_ELECTRON, spin: true, orbital: true },
    mass: { on: false, delta: 0.1, plus: [0], minus: nSites > 1 ? [1] : [] },
    shift: { on: false, shift: 0.05, element: "", orbitals: "" },
    scissor: { on: false, delta: 0.5, mesh: 0 },
    exchange: { on: false, energy: 0.1, direction: [0, 0, 1], element: "" },
    rashba: { on: false, lam: 0.05, orbitals: "pz" },
    efield: { on: false, E: [0, 0, 0.1] },
    soc: { on: false, element: "", family: "p", lam: 0.1, labels: "" },
  };
}

/** The modifier list of prayog_tb (applied in this order: gap changes first, the field last). */
export function modifiersOf(m: ModifyForm, reach?: number[]): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  if (m.soc?.on && m.soc.element.trim()) {                 // first: part of the material, before gap changes and fields
    const el = m.soc.element.trim();
    const labs = m.soc.labels.split(/[\s,]+/).filter(Boolean);
    out.push({ kind: "atomic_soc", lam: { [m.soc.family === "all" ? el : `${el}_${m.soc.family}`]: m.soc.lam },
      ...(labs.length ? { labels: { [el]: labs } } : {}) });
  }
  if (m.mass.on) {
    for (const i of m.mass.plus) out.push({ kind: "onsite_shift", shift: m.mass.delta / 2, sites: [i], label: "sublattice mass" });
    for (const i of m.mass.minus) out.push({ kind: "onsite_shift", shift: -m.mass.delta / 2, sites: [i], label: "sublattice mass" });
  }
  if (m.shift.on) {
    const mod: Record<string, unknown> = { kind: "onsite_shift", shift: m.shift.shift };
    if (m.shift.element.trim()) mod.element = m.shift.element.trim();
    const orbs = m.shift.orbitals.split(/[\s,]+/).filter(Boolean);
    if (orbs.length) mod.orbitals = orbs;
    out.push(mod);
  }
  if (m.scissor.on) {
    const mod: Record<string, unknown> = { kind: "scissor", delta: m.scissor.delta };
    if (m.scissor.mesh > 0) mod.mesh = [0, 1, 2].map((a) => (!reach || reach[a] > 0 ? m.scissor.mesh : 1));
    if ((m.scissor.nValence ?? 0) > 0) mod.n_valence = m.scissor.nValence;
    out.push(mod);
  }
  if (m.rashba?.on) {
    const orbs = m.rashba.orbitals.split(/[\s,]+/).filter(Boolean);
    out.push({ kind: "rashba", lam: m.rashba.lam, ...(orbs.length ? { orbitals: orbs } : {}) });
  }
  if (m.exchange?.on) out.push({ kind: "exchange", energy: m.exchange.energy, direction: m.exchange.direction.map(Number),
    ...(m.exchange.element.trim() ? { element: m.exchange.element.trim() } : {}) });
  if (m.efield?.on) out.push({ kind: "efield", E: m.efield.E.map(Number) });
  if (m.zeeman.on) out.push({ kind: "zeeman", B: m.zeeman.B.map(Number), g: m.zeeman.g, spin: m.zeeman.spin, orbital: m.zeeman.orbital });
  return out;
}

export function modifyProblems(m: ModifyForm, nSites: number, twoD = true): string[] {
  const out: string[] = [];
  if (!modifiersOf(m).length) out.push("Switch on at least one change.");
  if (m.mass.on) {
    if (!m.mass.plus.length || !m.mass.minus.length) out.push("Sublattice mass: choose the atoms of both sublattices.");
    if (m.mass.plus.some((i) => m.mass.minus.includes(i))) out.push("Sublattice mass: an atom cannot be on both sublattices.");
    if ([...m.mass.plus, ...m.mass.minus].some((i) => i < 0 || i >= nSites)) out.push("Sublattice mass: no such atom.");
  }
  if (m.zeeman.on && !nonZero(m.zeeman.B)) out.push("Zeeman: give a field B.");
  if (m.exchange?.on && !nonZero(m.exchange.direction)) out.push("Exchange field: give a direction.");
  if (m.efield?.on && !nonZero(m.efield.E)) out.push("Electric field: give E.");
  if (m.zeeman.on && !m.zeeman.spin && !m.zeeman.orbital) out.push("Zeeman: switch on the spin or the orbital term.");
  if (m.scissor.on && m.scissor.mesh && !(m.scissor.mesh >= 4 && m.scissor.mesh <= (twoD ? 48 : 16))) out.push(`Scissor: mesh from 4 to ${twoD ? 48 : 16} per direction.`);
  if (m.shift.on && !m.shift.element.trim() && !m.shift.orbitals.trim()) out.push("On-site shift: name an element or orbitals (or both).");
  if (m.soc?.on && !m.soc.element.trim()) out.push("Spin-orbit coupling: choose the element.");
  return out;
}

/** One line per modifier: "Zeeman B (0, 0, 10) T, spin + orbit", "scissor +0.5 eV", ... */
export function describeModifiers(mods: Record<string, any>[] | undefined): string[] {
  const out: string[] = [];
  const mass = (mods ?? []).filter((m) => m.label === "sublattice mass");
  if (mass.length) {
    const d = Math.max(...mass.map((m) => m.shift)) - Math.min(...mass.map((m) => m.shift));
    out.push(`sublattice mass Δ = ${+d.toPrecision(4)} eV`);
  }
  for (const m of mods ?? []) {
    if (m.label === "sublattice mass" || (m.kind === "spinful" && m.auto)) continue;
    if (m.kind === "zeeman") out.push(`Zeeman B ${vec(m.B)} T, ${[m.spin !== false && "spin", m.orbital !== false && "orbit"].filter(Boolean).join(" + ")}`);
    else if (m.kind === "scissor") out.push(`scissor ${m.delta >= 0 ? "+" : ""}${m.delta} eV`);
    else if (m.kind === "exchange") out.push(`exchange ${m.energy} eV along ${vec(m.direction)}${m.element ? ` on ${m.element}` : ""}`);
    else if (m.kind === "efield") out.push(`electric field E ${vec(m.E)} V/Å`);
    else if (m.kind === "rashba") out.push(`Rashba λ_R = ${m.lam} eV (${(m.orbitals ?? ["pz"]).join(" ")})`);
    else if (m.kind === "onsite_shift") out.push(`on-site ${m.shift >= 0 ? "+" : ""}${m.shift} eV${m.element ? ` on ${m.element}` : ""}${m.orbitals ? ` (${m.orbitals.join(" ")})` : ""}${m.sites ? ` (atoms ${m.sites.map((i: number) => i + 1).join(", ")})` : ""}`);
    else if (m.kind === "spinful") out.push("spin doubled");
    else if (m.kind === "atomic_soc") out.push(`atomic SOC ${Object.entries(m.lam ?? {}).map(([k, v]) => `λ(${k}) = ${v} eV`).join(", ")}${m.labels ? " (labels given)" : ""}`);
  }
  return out;
}

// -- band-edge tools (negf.band_edges, prayog_tb.analysis) --------------------------------------------------------
export type Analysis = "edges" | "bands" | "field" | "angle" | "efield";
export const ANALYSES: { value: Analysis; label: string }[] = [
  { value: "edges", label: "Edges" }, { value: "bands", label: "Band zoom" }, { value: "field", label: "Gap vs |B|" },
  { value: "angle", label: "Gap vs angle" }, { value: "efield", label: "Gap vs E" },
];
export interface EdgesForm {
  analysis: Analysis;
  B: number[];                                     // edges / band zoom: the field (T)
  sweep: { B_min: number; B_max: number; n: number; direction: "out-of-plane" | "in-plane" | "in-plane-2" };
  angles: { B: number; min: number; max: number; n: number; phi: number };
  esweep?: { E_min: number; E_max: number; n: number };   // gap vs a gate field across a 2D material (V/Å)
  g: number; spin: boolean; orbital: boolean;
  window: number;                                  // band zoom: eV around the edges
  valleys: "auto" | "none" | "custom";
  custom: string;                                  // "K = 2/3, 1/3, 0; K' = 1/3, 2/3, 0"
}
export function defaultEdges(): EdgesForm {
  return { analysis: "field", B: [0, 0, 10], sweep: { B_min: 0, B_max: 10, n: 6, direction: "out-of-plane" },
    angles: { B: 10, min: 0, max: 90, n: 7, phi: 0 }, esweep: { E_min: 0, E_max: 0.3, n: 7 }, g: G_ELECTRON, spin: true, orbital: true, window: 0.3,
    valleys: "auto", custom: "" };
}

const frac = (s: string) => {
  const t = s.trim();
  const m = /^(-?\d+(?:\.\d*)?)\s*\/\s*(\d+(?:\.\d*)?)$/.exec(t);
  return m ? Number(m[1]) / Number(m[2]) : t === "" ? NaN : Number(t);
};
/** "K = 2/3, 1/3, 0; K' = 1/3, 2/3, 0" -> {K: [...], K': [...]}; throws on a malformed entry. */
export function parseValleys(text: string): Record<string, number[]> {
  const out: Record<string, number[]> = {};
  for (const part of text.split(/[;\n]+/).map((p) => p.trim()).filter(Boolean)) {
    const [name, k] = part.split("=").map((x) => x?.trim());
    const v = (k ?? "").split(/[\s,]+/).filter(Boolean).map(frac);
    if (!name || v.length !== 3 || v.some((x) => !Number.isFinite(x))) throw new Error(`Valley "${part}": write name = k1, k2, k3 (fractions like 2/3 are fine).`);
    out[name] = v;
  }
  return out;
}

/** params.request of negf.band_edges. */
export function edgesRequest(f: EdgesForm): Record<string, unknown> {
  const req: Record<string, unknown> = { analysis: f.analysis, g: f.g, spin: f.spin, orbital: f.orbital,
    valleys: f.valleys === "auto" ? "auto" : f.valleys === "none" ? {} : parseValleys(f.custom) };
  if (f.analysis === "edges" || f.analysis === "bands") req.B = f.B.map(Number);
  if (f.analysis === "bands") req.window = f.window;
  if (f.analysis === "field") req.sweep = f.sweep;
  if (f.analysis === "efield") req.sweep = f.esweep ?? defaultEdges().esweep;
  if (f.analysis === "angle") {
    req.B = f.angles.B;
    req.angles = { min: f.angles.min, max: f.angles.max, n: f.angles.n, phi: f.angles.phi };
  }
  return req;
}

export function edgesProblems(f: EdgesForm): string[] {
  const out: string[] = [];
  if (f.valleys === "custom") {
    try { if (!Object.keys(parseValleys(f.custom)).length) out.push("Give at least one valley, or choose automatic."); }
    catch (e) { out.push((e as Error).message); }
  }
  if (f.analysis === "field") {
    if (f.sweep.B_max === f.sweep.B_min) out.push("The field range needs two different ends.");
    if (!(f.sweep.n >= 2)) out.push("At least 2 field points.");
  }
  if (f.analysis === "angle") {
    if (f.angles.max === f.angles.min) out.push("The angle range needs two different ends.");
    if (!(f.angles.n >= 2)) out.push("At least 2 angles.");
  }
  if (f.analysis === "efield") {
    const e = f.esweep ?? defaultEdges().esweep!;
    if (e.E_max === e.E_min) out.push("The field range needs two different ends.");
    if (!(e.n >= 2)) out.push("At least 2 field points.");
  }
  if (f.analysis === "bands" && !(f.window > 0)) out.push("The zoom window must be positive.");
  if (f.analysis !== "efield" && !f.spin && !f.orbital) out.push("Switch on the spin or the orbital Zeeman term.");
  return out;
}

export function describeEdges(params: Record<string, any>): string {
  const r = (params.request ?? {}) as Record<string, any>;
  if (r.analysis === "field") return `Gap vs |B|: ${r.sweep?.B_min}…${r.sweep?.B_max} T (${r.sweep?.n} points), ${r.sweep?.direction}`;
  if (r.analysis === "efield") return `Gap vs E: ${r.sweep?.E_min}…${r.sweep?.E_max} V/Å (${r.sweep?.n} points) across the layer`;
  if (r.analysis === "angle") return `Gap vs angle at ${r.B} T: ${r.angles?.min}…${r.angles?.max}° (${r.angles?.n} points)`;
  if (r.analysis === "bands") return `Band zoom${nonZero(r.B) ? ` at B ${vec(r.B)} T` : ""}`;
  return `Band edges${nonZero(r.B) ? ` at B ${vec(r.B)} T` : ""}`;
}

/** The energy columns of a sweep's rows: [key, label] for the global edges and each valley. */
function edgeColumns(r: Record<string, any>, kind: "edges" | "gaps"): [string, string][] {
  const valleys = Object.keys(r.valleys ?? {});
  if (kind === "gaps") return [["gap", "gap"], ["direct_gap", "smallest direct gap"], ...valleys.map((v) => [`${v}:gap`, `gap at ${v}`] as [string, string])];
  return [["vbm", "VBM"], ["cbm", "CBM"], ...valleys.flatMap((v) => [[`${v}:vb`, `VB top at ${v}`], [`${v}:cb`, `CB bottom at ${v}`]] as [string, string][])];
}

/** Changes from the first row (meV) of the edges or the gaps against B or the angle: Zeeman shifts are meV on eV
 * scales, so the shift is what is drawn; the hover gives the energy itself. */
export function sweepFigure(r: Record<string, any>, kind: "edges" | "gaps") {
  const rows = (r.rows ?? []) as Record<string, any>[];
  const xKey = r.x ?? "B";
  const x = rows.map((row) => row[xKey]);
  const colors = ["#2e213e", "#eb6834", "#2a78d6", "#1baf7a", "#e87ba4", "#eda100", "#4a3aa7", "#e34948"];
  const data = edgeColumns(r, kind).filter(([k]) => rows.length && typeof rows[0][k] === "number").map(([k, label], i) => ({
    type: "scatter", mode: "lines+markers", name: label, x, y: rows.map((row) => 1000 * (row[k] - rows[0][k])),
    customdata: rows.map((row) => row[k]), hovertemplate: `${label}: %{customdata:.5f} eV (%{y:+.3f} meV)<extra></extra>`,
    line: { color: colors[i % colors.length], dash: k.includes(":") ? "dash" : "solid" }, marker: { size: 5 },
  }));
  const xTitle = xKey === "B" ? `|B| (T) along ${vec(r.direction ?? [0, 0, 1])}`
    : xKey === "E" ? "E across the layer (V/Å)" : `angle from the normal θ (°) at ${r.B} T`;
  const g0 = rows[0]?.gap;
  const title = kind === "gaps" ? `Gap change${g0 != null ? ` (gap ${(+g0).toFixed(4)} eV at the start)` : ""}` : "Band-edge shifts";
  return { data, layout: { title: { text: title }, xaxis: { title: { text: xTitle } },
    yaxis: { title: { text: "change from the first point (meV)" } }, legend: { orientation: "h", y: -0.32 },
    margin: { l: 60, r: 20, t: 40, b: 90 } } };
}

/** g factors (and slopes) of every edge and gap of a |B| sweep, for a table. */
export function slopeRows(r: Record<string, any>): { key: string; slope: number; g: number }[] {
  return Object.entries((r.slopes ?? {}) as Record<string, any>).map(([key, v]) => ({ key, slope: v.slope_meV_per_T, g: v.g }));
}

const spinColor = (s?: number[]) => (!s ? "#2e213e" : s[2] > 0.3 ? "#c0392b" : s[2] < -0.3 ? "#2471a3" : "#7d7d85");

/** Valley levels: the top valence and bottom conduction levels in each valley (short bars; red spin up, blue down).
 * Valence below, conduction above, each on its own axis so meV splittings stay visible. */
export function valleyLevelsFigure(r: Record<string, any>) {
  const ve = (r.valley_edges ?? {}) as Record<string, any>;
  const names = Object.keys(ve);
  const data: unknown[] = [];
  names.forEach((name, i) => {
    const v = ve[name];
    (["vb", "cb"] as const).forEach((band) => {
      const levels = (v[`${band}_levels`] ?? []) as number[];
      const spins = (v[`${band}_spins`] ?? []) as number[][];
      levels.forEach((E, n) => data.push({ type: "scatter", mode: "lines", x: [i - 0.3, i + 0.3], y: [E, E], yaxis: band === "cb" ? "y2" : "y",
        line: { color: spinColor(spins[n]), width: 3 }, hovertemplate: `${name} ${band === "vb" ? "valence" : "conduction"}: ${E.toFixed(5)} eV`
          + `${spins[n] ? `<br>⟨σ⟩ (${spins[n].map((s) => s.toFixed(2)).join(", ")})` : ""}<extra></extra>`, showlegend: false }));
    });
  });
  return { data, layout: { title: { text: "Valley levels (red ↑, blue ↓ along z)" },
    xaxis: { tickvals: names.map((_, i) => i), ticktext: names, range: [-0.6, names.length - 0.4], zeroline: false, showgrid: false },
    yaxis: { domain: [0, 0.45], title: { text: "valence (eV)" }, tickformat: ".4f" },
    yaxis2: { domain: [0.55, 1], title: { text: "conduction (eV)" }, tickformat: ".4f" }, margin: { l: 80, r: 20, t: 40, b: 40 } } };
}

/** Bands along the path zoomed on the edges: without the field (grey) and with it (colour). A gap wider than the
 * zoom window is drawn as two panels (conduction above, valence below) so both edges are magnified. */
export function bandZoomFigure(r: Record<string, any>) {
  const e = r.edges_zero_field ?? r.edges;
  const ticks = (r.ticks ?? []) as [number, string][];
  const [lo, hi] = (r.window ?? [-1, 1]) as number[];
  const w = e ? Math.max(e.vbm.E - lo, hi - e.cbm.E) : 0;
  const split = !!e && e.cbm.E - e.vbm.E > 1.5 * w;
  const traces = (axis?: string) => {
    const out: any[] = [bandTrace(r.bands.x, r.bands.energies, "B = 0", r.bands_field ? "#9a9aa0" : "#2e213e", r.bands_field ? 2.5 : 1.4)];
    if (r.bands_field) out.push(bandTrace(r.bands_field.x, r.bands_field.energies, `B ${vec(r.B)} T`, "#eb6834", 1.1));
    return out.map((t) => ({ ...t, yaxis: axis, showlegend: axis !== "y2" }));
  };
  const data: unknown[] = split ? [...traces("y"), ...traces("y2")] : traces();
  const shapes: any[] = ticks.map(([x]) => ({ type: "line", x0: x, x1: x, yref: "paper", y0: 0, y1: 1, line: { color: "#bbb", width: 1 } }));
  const xaxis = { tickvals: ticks.map((t) => t[0]), ticktext: ticks.map((t) => t[1]), showgrid: false, zeroline: false };
  const title = { text: r.bands_field ? `Bands near the gap: B = 0 and B ${vec(r.B)} T` : "Bands near the gap" };
  if (!split) return { data, layout: { title, shapes, xaxis, yaxis: { title: { text: "E − E<sub>F</sub> (eV)" }, range: r.window },
    legend: { orientation: "h", y: -0.15 } } };
  return { data, layout: { title, shapes, xaxis,
    yaxis: { domain: [0, 0.47], range: [lo, e.vbm.E + w / 3], title: { text: "valence (eV)" }, tickformat: ".3f" },
    yaxis2: { domain: [0.53, 1], range: [e.cbm.E - w / 3, hi], title: { text: "conduction (eV)" }, tickformat: ".3f" },
    legend: { orientation: "h", y: -0.15 }, margin: { l: 70, r: 20, t: 40, b: 50 } } };
}

export const fmtE = (E: number | undefined) => (E == null ? "–" : `${E.toFixed(4)} eV`);
export const fmtK = (k: number[] | undefined) => (k ? `(${k.map((x) => +x.toFixed(4)).join(", ")})` : "–");
export const gFromSlope = (slope_meV_per_T: number) => slope_meV_per_T / MU_B_MEV;
